import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { Store } from '@ngrx/store';

import { Category, FinnairItineraryItemFlight, FinnairPassengerItem } from '@fcom/dapi/api/models';
import { cartPassengersSelector } from '@fcom/booking/store/selectors';
import { ServiceSelections } from '@fcom/dapi/interfaces';

import {
  ServicesActions,
  nonSeatBundleConstituentsByFlightSelector,
  nonSeatBundlesByFlightAndPassengerSelector,
  serviceCategoryTranslationsMapSelector,
  serviceSelections,
} from '../../../../store';
import { asTravelerService } from '../../../../store/utils/services.utils';

@Component({
  selector: 'fin-ancillary-bundle-selection',
  templateUrl: './ancillary-bundle-selection.component.html',
  styleUrls: ['./ancillary-bundle-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AncillaryBundleSelectionComponent {
  readonly category = input.required<Category>();
  readonly flight = input.required<FinnairItineraryItemFlight>();

  private store$ = inject(Store);

  private selections = this.store$.selectSignal(serviceSelections);

  private translationsByCategory = this.store$.selectSignal(serviceCategoryTranslationsMapSelector);
  readonly translations = computed(() => this.translationsByCategory()[this.category()]);

  private cartPassengers = this.store$.selectSignal(cartPassengersSelector);
  readonly passengers = computed(() => this.cartPassengers() || []);

  private bundleAvailabilityByCategory = this.store$.selectSignal(nonSeatBundlesByFlightAndPassengerSelector);
  readonly availabilityByPax = computed(
    () => this.bundleAvailabilityByCategory()[this.category()]?.[this.flight()?.id] ?? {}
  );

  private constituentsByCategory = this.store$.selectSignal(nonSeatBundleConstituentsByFlightSelector);
  readonly constituents = computed(() => this.constituentsByCategory()[this.category()]?.[this.flight()?.id] ?? []);

  readonly selectionsByPax = computed(() => {
    const selectionsForCategory = this.selections()?.[this.category() as keyof ServiceSelections];
    return selectionsForCategory?.[this.flight()?.id] ?? {};
  });

  addCombo(passenger: FinnairPassengerItem): void {
    this.store$.dispatch(
      ServicesActions.setSelectionForFragmentForTraveler({
        category: this.category(),
        fragmentId: this.flight().id,
        travelerId: passenger.id,
        selection: asTravelerService(this.availabilityByPax()[passenger.id].bundleItem),
      })
    );
  }

  removeCombo(passenger: FinnairPassengerItem): void {
    this.store$.dispatch(
      ServicesActions.setSelectionForFragmentForTraveler({
        category: this.category(),
        fragmentId: this.flight().id,
        travelerId: passenger.id,
        selection: undefined,
      })
    );
  }
}
